import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import ProductCard from "@/components/ProductCard";
import { getRecentlyViewed } from "@/context/CartContext";
import AgeGate, { isAgeVerified } from "@/components/AgeGate";
import { CaretLeft, CaretRight, ArrowRight, Fire, Star, Clock, Sparkle, TrendUp, Tag } from "@phosphor-icons/react";

const bannerLink = (b) => {
  if (b.target_type === "product" && b.target_value) return `/producto/${b.target_value}`;
  if (b.target_type === "category" && b.target_value) return `/?category=${b.target_value}`;
  return b.target_value || "/";
};

const ICONS = { ofertas: Fire, productos_destacados: Star, recomendados: Sparkle, mas_vendidos: TrendUp, nuevos_productos: Sparkle, vistos_recientemente: Clock, promociones_especiales: Fire };

function ProductRow({ title, icon: Icon, products, testid }) {
  if (!products || !products.length) return null;
  return (
    <div className="bg-white rounded-xl nb-shadow p-4 sm:p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">{Icon && <Icon weight="fill" size={22} className="text-primary" />}<h2 className="text-lg sm:text-xl font-extrabold tracking-tight">{title}</h2></div>
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
        {products.map((p) => <div key={p.id} className="w-44 sm:w-52 shrink-0"><ProductCard product={p} /></div>)}
      </div>
    </div>
  );
}

function HeroCarousel({ banners }) {
  const [slide, setSlide] = useState(0);
  useEffect(() => { if (banners.length < 2) return; const t = setInterval(() => setSlide((s) => (s + 1) % banners.length), 5000); return () => clearInterval(t); }, [banners.length]);
  if (!banners.length) return null;
  return (
    <section className="relative rounded-xl overflow-hidden nb-shadow group" data-testid="hero-carousel">
      <div className="relative h-[220px] sm:h-[300px] lg:h-[360px]">
        {banners.map((b, i) => (
          <Link key={b.id} to={bannerLink(b)} data-testid={`hero-slide-${b.id}`}
                className={`absolute inset-0 transition-opacity duration-700 ${i === slide ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}>
            <img src={fileUrl(b.image_desktop)} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/25 to-transparent" />
            <div className="relative h-full flex flex-col justify-center px-6 sm:px-12 max-w-[65%]">
              {b.title && <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">{b.title}</h2>}
              {b.subtitle && <p className="mt-2 text-sm sm:text-base text-white/90">{b.subtitle}</p>}
              {b.has_button && <span className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold text-sm px-5 py-2.5 rounded-full w-fit">{b.button_text || "Ver más"} <ArrowRight weight="bold" size={16} /></span>}
            </div>
          </Link>
        ))}
      </div>
      {banners.length > 1 && (<>
        <button onClick={() => setSlide((slide - 1 + banners.length) % banners.length)} data-testid="hero-prev" className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"><CaretLeft weight="bold" size={18} /></button>
        <button onClick={() => setSlide((slide + 1) % banners.length)} data-testid="hero-next" className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"><CaretRight weight="bold" size={18} /></button>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">{banners.map((_, i) => <button key={i} onClick={() => setSlide(i)} className={`h-2 rounded-full transition-all ${i === slide ? "w-6 bg-primary" : "w-2 bg-white/70"}`} />)}</div>
      </>)}
    </section>
  );
}

export default function Marketplace() {
  const [home, setHome] = useState(null);
  const [recent, setRecent] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [ageOk, setAgeOk] = useState(isAgeVerified());
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeCat = searchParams.get("category") || "all";
  const query = searchParams.get("q") || "";
  const onOffer = searchParams.get("on_offer") === "1";
  const isFiltering = !!query || activeCat !== "all" || onOffer;

  useEffect(() => {
    api.get("/categories").then((r) => { setAllCategories(r.data); setCategories(r.data.filter((c) => !c.parent_id)); });
  }, []);

  useEffect(() => {
    if (isFiltering) return;
    api.get("/home").then((r) => setHome(r.data));
    const rv = getRecentlyViewed();
    if (rv.length) api.post("/products/by-ids", { ids: rv }).then((r) => setRecent(r.data));
    else setRecent([]);
  }, [isFiltering]);

  const fetchProducts = useCallback(async () => {
    if (!isFiltering) return;
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (activeCat !== "all") params.category = activeCat;
      if (onOffer) params.on_offer = true;
      const res = await api.get("/products", { params });
      setProducts(res.data);
    } finally { setLoading(false); }
  }, [query, activeCat, onOffer, isFiltering]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setCategory = (cat) => { const p = new URLSearchParams(); if (cat !== "all") p.set("category", cat); setSearchParams(p); };
  const catName = categories.find((c) => c.id === activeCat)?.name;
  const activeCatAdult = allCategories.find((c) => c.id === activeCat)?.is_adult;

  const renderSection = (s) => {
    if (s.type === "banners_principales") return <HeroCarousel key={s.key} banners={s.data} />;
    if (s.type === "banners_secundarios") return (
      <section key={s.key} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="promo-banners">
        {s.data.map((b) => (
          <Link key={b.id} to={bannerLink(b)} data-testid={`promo-${b.id}`} className="relative rounded-xl overflow-hidden h-40 nb-shadow group">
            <img src={fileUrl(b.image_desktop)} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-center px-6 max-w-[70%]">
              {b.title && <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight leading-tight">{b.title}</h3>}
              {b.subtitle && <p className="text-sm text-white/85 mt-1">{b.subtitle}</p>}
              {b.has_button && <span className="mt-3 text-sm font-semibold text-[#FFE600] inline-flex items-center gap-1">{b.button_text} <ArrowRight weight="bold" size={14} /></span>}
            </div>
          </Link>
        ))}
      </section>
    );
    if (s.type === "categorias_destacadas") return (
      <section key={s.key} data-testid="cat-shortcuts">
        <h2 className="text-lg font-extrabold tracking-tight mb-3">{s.title}</h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {s.data.slice(0, 12).map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`shortcut-${c.name}`} className="meli-card p-3 sm:p-4 text-center flex flex-col items-center">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-secondary flex items-center justify-center">{c.image ? <img src={fileUrl(c.image)} alt={c.name} className="w-full h-full object-cover" /> : <Tag weight="bold" size={22} className="text-primary" />}</div>
              <span className="mt-2 text-xs font-semibold line-clamp-1">{c.name}</span>
            </button>
          ))}
        </div>
      </section>
    );
    if (s.type === "vistos_recientemente") return <ProductRow key={s.key} title={s.title} icon={Clock} products={recent} testid="recently-viewed" />;
    // all product-type sections
    return <ProductRow key={s.key} title={s.title} icon={ICONS[s.type] || Star} products={s.data} testid={`section-${s.key}`} />;
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      {activeCatAdult && !ageOk && <AgeGate onAccept={() => setAgeOk(true)} />}
      <div className="bg-white border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-5 py-2 flex gap-2 items-center overflow-x-auto no-scrollbar">
          <button onClick={() => setCategory("all")} data-testid="category-all" className={`text-[13px] font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${activeCat === "all" && !onOffer ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-secondary"}`}>Inicio</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`category-${c.name}`} className={`text-[13px] font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${activeCat === c.id ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-secondary"}`}>{c.name}</button>
          ))}
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-3 sm:px-5 py-5 space-y-6">
        {!isFiltering ? (
          home ? home.sections.map(renderSection) : <div className="h-96 bg-white rounded-xl animate-pulse" />
        ) : (
          <section className="bg-white rounded-xl nb-shadow p-4 sm:p-6">
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">{query ? `Resultados para "${query}"` : onOffer ? "Ofertas" : catName || "Productos"}</h2>
              <span className="text-sm text-muted-foreground">{products.length} productos</span>
            </div>
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">{[...Array(10)].map((_, i) => <div key={i} className="h-72 rounded-lg bg-secondary animate-pulse" />)}</div>
            ) : products.length === 0 ? (
              <div className="py-16 text-center" data-testid="empty-state"><p className="text-lg font-bold">No encontramos productos</p><p className="text-muted-foreground mt-1">Prueba con otra búsqueda o categoría.</p></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">{products.map((p) => <ProductCard key={p.id} product={p} />)}</div>
            )}
          </section>
        )}
      </main>

      <footer className="bg-white border-t border-black/5 mt-8"><div className="max-w-[1200px] mx-auto px-5 py-8 text-center text-sm text-muted-foreground"><p className="font-bold text-foreground">Todito Ventas</p><p className="mt-1">Tienda oficial · Compra segura</p></div></footer>
    </div>
  );
}
